import { z } from 'zod';
import {
  GLOBAL_RULES_BLOCK_MAX,
  GLOBAL_RULE_TEXT_MAX,
  GUARDRAIL_CONSECUTIVE_TURNS_MAX,
  GUARDRAIL_DETECTION_MAX,
  GUARDRAIL_DIRECTION_MAX,
  guardrailActionSchema,
  guardrailSchema,
} from './rules.js';
import {
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionedContentSchema,
  versionedDetailSchema,
} from './versioning.js';

/**
 * « Les règles » (vitrine.md section 5, issue #192): the global rules and the
 * guardrails of Réglages > Règles - what `global_rules` and `guardrails` held
 * in place until the issue - as the SEVENTH consumer of the versioning pattern
 * and the third SINGLETON, at the patron of the texts of the noyaux
 * (`noyau-texts.ts`) and of the models (`model-settings.ts`).
 *
 * The two lists travel TOGETHER in one version: the mockup replaces the two
 * saves of the old screen by the one gesture of the head, and a session freezes
 * one version of both.
 *
 * ## The shape of a version
 *
 * `rules` is ordered, and THE ORDER OF THE ARRAY IS THE ORDER OF THE PRIORITIES
 * (§8.5): a version stores no `priority` that could disagree with it, and the
 * engine's `GlobalRule` is built on the way out, the index being the priority.
 * `guardrails` keep their reading order, with the shape of the table. Every row
 * keeps its identifier, the one the rows of the tables had, so that what a turn
 * recorded about a rule still names it across versions.
 */

/** The identifier of the singleton entity - the one `{id}` its routes serve. */
export const RULE_SETTINGS_ID = 'rule-settings';

/** The fixed name the layer wants on the entity and in every version (§4.1). */
export const RULE_SETTINGS_NAME = 'Les règles';

const ruleSettingsNameSchema = z.literal(RULE_SETTINGS_NAME).default(RULE_SETTINGS_NAME);

/**
 * One global rule of a version: its identifier and its text. The priority is
 * the position in the list.
 */
export const ruleEntrySchema = z
  .object({
    id: z.string().min(1),
    // Trimmed like the block of the old `PUT /api/rules` (`globalRuleBlockRowSchema`):
    // a text of spaces is no direction, and is refused here as it was there.
    text: z.string().trim().min(1).max(GLOBAL_RULE_TEXT_MAX),
  })
  .strict();

/**
 * One guardrail of a version: `guardrailSchema` with the bounds the old writes
 * imposed (`guardrailWriteSchema`) - the detection trimmed, so a detection of
 * spaces is refused - and nothing else.
 */
const guardrailEntrySchema = guardrailSchema.extend({
  detection: z.string().trim().min(1).max(GUARDRAIL_DETECTION_MAX),
});
export type RuleEntry = z.infer<typeof ruleEntrySchema>;

/**
 * The CONTENT of one version: the rules by priority, the guardrails in reading
 * order. Both lists may be empty - « un dispositif sans garde-fou est un état
 * légal » (§11.3), and so is one without rule.
 */
export const ruleSettingsContentSchema = z.object({
  name: ruleSettingsNameSchema,
  rules: z.array(ruleEntrySchema).max(GLOBAL_RULES_BLOCK_MAX),
  guardrails: z.array(guardrailEntrySchema),
});
export type RuleSettingsContent = z.infer<typeof ruleSettingsContentSchema>;

/** A rule as a DRAFT holds it: the text may be empty while it is typed. */
const draftRuleEntrySchema = z
  .object({ id: z.string().min(1), text: z.string().max(GLOBAL_RULE_TEXT_MAX) })
  .strict();

/** An action as a DRAFT holds it: the injected direction may still be empty. */
const draftActionSchema = z.union([
  z
    .object({
      kind: z.literal('inject-direction'),
      text: z.string().max(GUARDRAIL_DIRECTION_MAX),
    })
    .strict(),
  guardrailActionSchema,
]);

/** A guardrail as a DRAFT holds it: the detection and the direction may be empty. */
const draftGuardrailSchema = z
  .object({
    id: z.string().min(1),
    detection: z.string().max(GUARDRAIL_DETECTION_MAX),
    consecutiveTurns: z.number().int().min(1).max(GUARDRAIL_CONSECUTIVE_TURNS_MAX),
    action: draftActionSchema,
  })
  .strict();

/** The content of a DRAFT: the same two lists, with the rows a version would still refuse. */
export const ruleSettingsDraftContentSchema = z.object({
  name: ruleSettingsNameSchema,
  rules: z.array(draftRuleEntrySchema).max(GLOBAL_RULES_BLOCK_MAX),
  guardrails: z.array(draftGuardrailSchema),
});
export type RuleSettingsDraftContent = z.infer<typeof ruleSettingsDraftContentSchema>;

/** Everything the editor of Réglages > Règles reads in one call. */
export const ruleSettingsDetailSchema = versionedDetailSchema(
  ruleSettingsContentSchema,
  ruleSettingsDraftContentSchema,
);
export type RuleSettingsDetail = z.infer<typeof ruleSettingsDetailSchema>;

/** One version read in full, for « Voir » and « Repartir de cette version ». */
export const ruleSettingsVersionSchema = versionedContentSchema(ruleSettingsContentSchema);
export type RuleSettingsVersion = z.infer<typeof ruleSettingsVersionSchema>;

/** « Enregistrer une nouvelle version » of the rules. */
export const ruleSettingsSaveVersionSchema = saveVersionRequestSchema(ruleSettingsContentSchema);
export type RuleSettingsSaveVersion = z.infer<typeof ruleSettingsSaveVersionSchema>;

/** An autosave of the draft of the rules. */
export const ruleSettingsSaveDraftSchema = saveDraftRequestSchema(ruleSettingsDraftContentSchema);
export type RuleSettingsSaveDraft = z.infer<typeof ruleSettingsSaveDraftSchema>;
